import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, firstValueFrom } from 'rxjs';
import {
  HttpHelpService,
  NotificationInboxFilters,
  NotificationSource,
  UserNotificationItem,
  UserNotificationType,
} from 'src/app/Data/service/http-help.service';
import { PushDeviceService } from 'src/app/shared/services/push-device.service';

const PAGE_SIZE = 20;

@Component({
  selector: 'app-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  items: UserNotificationItem[] = [];
  total = 0;
  unread = 0;
  page = 1;
  loading = false;
  error: string | null = null;

  readFilter: '' | 'unread' | 'read' = '';
  typeFilter = '';
  sourceFilter = '';

  officeIdNumber: number | null = null;
  companyRequestsCount = 0;

  readonly typeOptions: UserNotificationType[] = [
    'new_task',
    'new_message',
    'file',
    'mention',
    'merchant_reply',
    'task_closed',
    'task_reopened',
    'postponement_expired',
    'task_assigned',
    'participant_added',
    'broadcast',
    'expense_analysis_complete',
    'subscription_renewal_failed',
    'subscription_payment',
    'office_permission',
    'accountant_access_granted',
    'closed_month',
    'custom_payment',
    'other',
  ];

  readonly sourceOptions: NotificationSource[] = [
    'system',
    'accountant',
    'employee',
    'admin',
    'customer',
  ];

  private unreadSub?: Subscription;

  constructor(
    private readonly http: HttpHelpService,
    private readonly pushDevice: PushDeviceService,
    private readonly router: Router,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.officeIdNumber = this.readOfficeId();
    void this.loadPage(1, true);
    if (this.officeIdNumber != null) {
      void this.loadCompanyRequestsCount();
    }
    this.unreadSub = this.pushDevice.unreadCount$.subscribe((count) => {
      this.unread = count;
    });
  }

  ngOnDestroy(): void {
    this.unreadSub?.unsubscribe();
  }

  get hasMore(): boolean {
    return this.items.length < this.total;
  }

  get isOfficeUser(): boolean {
    return this.officeIdNumber != null;
  }

  get filters(): NotificationInboxFilters {
    const filters: NotificationInboxFilters = {};
    if (this.readFilter === 'unread') {
      filters.isRead = false;
    } else if (this.readFilter === 'read') {
      filters.isRead = true;
    }
    if (this.typeFilter) {
      filters.notificationType = this.typeFilter;
    }
    if (this.sourceFilter) {
      filters.notificationSource = this.sourceFilter;
    }
    return filters;
  }

  get hasActiveFilters(): boolean {
    return !!(this.readFilter || this.typeFilter || this.sourceFilter);
  }

  async loadPage(page: number, reset = false): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const res = await firstValueFrom(
        this.http.getMyNotifications(page, PAGE_SIZE, this.filters),
      );
      const newItems = reset ? res.data : [...this.items, ...res.data];
      this.items = newItems;
      this.total = res.total ?? 0;
      this.unread = res.unread ?? 0;
      this.page = page;
      this.pushDevice.setInboxUnreadCount(this.unread);
    } catch (err: any) {
      this.error = String(err?.status || 'network');
    } finally {
      this.loading = false;
    }
  }

  async loadCompanyRequestsCount(): Promise<void> {
    try {
      const res = await firstValueFrom(this.http.getAllPendingPermissionsByOffice(1, 0));
      this.companyRequestsCount = typeof res?.total === 'number' ? res.total : (res?.count ?? 0);
    } catch {
      this.companyRequestsCount = 0;
    }
  }

  applyFilters(): void {
    void this.loadPage(1, true);
  }

  clearFilters(): void {
    this.readFilter = '';
    this.typeFilter = '';
    this.sourceFilter = '';
    void this.loadPage(1, true);
  }

  loadMore(): void {
    if (this.loading || !this.hasMore) {
      return;
    }
    void this.loadPage(this.page + 1);
  }

  async markAllRead(): Promise<void> {
    await this.pushDevice.markAllRead();
    this.items = this.items.map((item) => ({ ...item, is_read: true }));
    this.unread = 0;
  }

  async onItemClick(item: UserNotificationItem): Promise<void> {
    const updated = await this.pushDevice.openNotificationItem(item);
    const idx = this.items.findIndex((n) => n.id === updated.id);
    if (idx >= 0) {
      this.items[idx] = updated;
    }
  }

  goToSettings(): void {
    void this.router.navigate(['/dashboard/notification-details']);
  }

  goToRequests(): void {
    void this.router.navigate(['/dashboard/notification-details'], {
      queryParams: { tab: 'requests' },
    });
  }

  getTypeIcon(type: UserNotificationType | null | undefined): string {
    const icons: Record<string, string> = {
      new_task: 'clipboard',
      new_message: 'message-circle',
      file: 'paperclip',
      mention: 'at-sign',
      merchant_reply: 'corner-up-left',
      task_closed: 'check-circle',
      task_reopened: 'rotate-ccw',
      postponement_expired: 'clock',
      task_assigned: 'user-check',
      participant_added: 'users',
      broadcast: 'radio',
      expense_analysis_complete: 'file-text',
      subscription_renewal_failed: 'alert-circle',
      subscription_payment: 'credit-card',
      office_permission: 'shield',
      accountant_access_granted: 'unlock',
      closed_month: 'calendar',
      custom_payment: 'dollar-sign',
    };
    return icons[type || ''] || 'bell';
  }

  typeLabel(type: UserNotificationType | null | undefined): string {
    if (!type) {
      return this.translate.instant('notifications.center.typeOther');
    }
    const key = `notifications.center.types.${type}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : type;
  }

  sourceLabel(source: NotificationSource | null | undefined): string {
    if (!source) {
      return this.translate.instant('notifications.center.sourceSystem');
    }
    const key = `notifications.center.sources.${source}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : source;
  }

  trackById(_: number, item: UserNotificationItem): number {
    return item.id;
  }

  private readOfficeId(): number | null {
    try {
      const raw = localStorage.getItem('office_id');
      if (!raw || raw === 'null') return null;
      const parsed = JSON.parse(raw);
      return parsed != null ? Number(parsed) : null;
    } catch {
      return null;
    }
  }
}
